import { useState } from 'preact/hooks';
import type { AuthOrg } from '../auth.js';

export function Install({ org }: { org: AuthOrg }): preact.ComponentChildren {
  const placeholderKey = 'whk_<your-key-from-the-Keys-tab>';
  const snippet = `<script\n  src="https://cdn.wholisphere.ai/embed.js"\n  data-key="${placeholderKey}"\n  data-position="bottom-right"\n  data-wholisphere\n></script>`;
  const githubAction = `# .github/workflows/deploy.yml\njobs:\n  deploy:\n    steps:\n      - uses: actions/checkout@v4\n      - run: ./deploy.sh\n      - uses: wholisphere/github-action@v1\n        with:\n          org-key: \${{ secrets.WHOLISPHERE_ORG_KEY }}\n          urls: '*'\n          reason: \${{ github.ref_name }}`;
  const mcpConfig = `{\n  "mcpServers": {\n    "wholisphere": {\n      "command": "npx",\n      "args": ["-y", "@wholisphere/mcp"],\n      "env": { "WHOLISPHERE_API_KEY": "whk_<your-key>" }\n    }\n  }\n}`;

  const [copied, setCopied] = useState<'snippet' | 'gha' | 'mcp' | null>(null);
  const copy = async (text: string, which: 'snippet' | 'gha' | 'mcp') => {
    await navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <>
      <div class="page-header">
        <h1>Install for {org.name}</h1>
        <p>
          One snippet to install on any site. Then wire your CI and (optionally) Claude Desktop.
        </p>
      </div>

      <div class="banner info">
        <strong>Step 0:</strong> create an API key on the{' '}
        <button
          type="button"
          class="btn-link inline"
          onClick={() => window.dispatchEvent(new CustomEvent('whs-nav', { detail: 'keys' }))}
        >
          Keys tab
        </button>
        , copy the plaintext, and substitute it for <code>{placeholderKey}</code> below.
      </div>

      <div class="card">
        <h2>1. Embed the agent</h2>
        <p class="desc">
          Drop this single tag into your site's &lt;head&gt;. Visitors get the floating
          accessibility tools immediately.
        </p>
        <pre class="snippet" tabindex={0}>
          {snippet}
        </pre>
        <button
          type="button"
          onClick={() => void copy(snippet, 'snippet')}
          style={{ marginTop: 12 }}
        >
          {copied === 'snippet' ? 'Copied ✓' : 'Copy embed snippet'}
        </button>
      </div>

      <div class="card">
        <h2>2. Wire the GitHub Action</h2>
        <p class="desc">
          Auto-invalidate the cache on every production deploy. Add the API key to repository
          secrets first.
        </p>
        <pre class="snippet" tabindex={0}>
          {githubAction}
        </pre>
        <button
          type="button"
          class="secondary"
          onClick={() => void copy(githubAction, 'gha')}
          style={{ marginTop: 12 }}
        >
          {copied === 'gha' ? 'Copied ✓' : 'Copy YAML'}
        </button>
      </div>

      <div class="card">
        <h2>3. (Optional) Use Wholisphere from Claude Desktop / Cursor</h2>
        <p class="desc">
          Add the MCP server to your AI tool's config so any chat can describe images, simplify
          text, summarize pages, and interpret voice commands.
        </p>
        <pre class="snippet" tabindex={0}>
          {mcpConfig}
        </pre>
        <button
          type="button"
          class="secondary"
          onClick={() => void copy(mcpConfig, 'mcp')}
          style={{ marginTop: 12 }}
        >
          {copied === 'mcp' ? 'Copied ✓' : 'Copy MCP config'}
        </button>
      </div>

      <div class="card">
        <h2>Org metadata</h2>
        <div class="meta">
          org id: <code>{org.id}</code>
        </div>
        <div class="meta">
          slug: <code>{org.slug}</code>
        </div>
        <div class="meta">
          plan: <code>{org.plan}</code>
        </div>
        <div class="meta">
          region: <code>{org.region}</code>
        </div>
      </div>
    </>
  );
}
